import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { useFormik } from 'formik'
import { Button } from '@dgshahr/ui-kit'
import Switch from '@dgshahr/ui-kit/Form/Switch'
import Drawer from '@dgshahr/ui-kit/Drawer'

import { useButtonActionsContext } from '@/utils/contexts/form-layout/ButtonActionsContext'
import { useGetProvinces } from '@/api/provinces/GetProvinces'
import { useGetStepCorrections } from '@/api/step-correction/StepCorrection'
import { getAddressFormSchema } from '@/utils/validations/address/addressStepSchema'
import { useScrollToFormikError } from '@/hooks/useScrollToFormikError'

import { IFormValues } from './FormAddress'
import WarehouseAddressForm from './WarehouseAddressForm'
import OfficeAddressForm from './OfficeAddressForm'

interface IAddressFormProps {
  initialValues: IFormValues
  onSubmit: (values: IFormValues) => void
}

const AddressForm = ({ initialValues, onSubmit }: IAddressFormProps) => {
  const { setButtonActions } = useButtonActionsContext()
  const navigate = useNavigate()
  const [showWarehouseAddress, setShowWarehouseAddress] = useState(
    initialValues.is_warehouse
  )

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const { data: provinces } = useGetProvinces({})

  const { data: correction } = useGetStepCorrections({ step: 'ADDRESS' })

  useEffect(() => {
    setShowWarehouseAddress(initialValues.is_warehouse)
  }, [initialValues.is_warehouse])

  const schema = getAddressFormSchema(showWarehouseAddress)

  const formik = useFormik({
    initialValues,
    validationSchema: toFormikValidationSchema(schema),
    validateOnChange: true,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit
  })

  useScrollToFormikError(formik)

  const handleSubmitAllForms = async () => {
    await formik.setTouched(
      {
        provinces: true,
        cities: true,
        address: true,
        detail: true,
        postalCode: true,
        ...(showWarehouseAddress && {
          warehouseProvinces: true,
          warehouseCities: true,
          warehouseAddress: true,
          warehouseDetail: true,
          warehousePostalCode: true
        })
      },
      false
    )
    await formik.submitForm()
  }

  const handleWarehouseCheckboxChange = (checked: boolean) => {
    setShowWarehouseAddress(checked)
    formik.setFieldValue('is_warehouse', checked)
  }

  useEffect(() => {
    formik.validateForm()
  }, [showWarehouseAddress])

  useEffect(() => {
    if (correction?.length > 0) {
      setButtonActions((prevState) => ({
        ...prevState,
        prevStepDisabled: false,
        prevStepAction: () => setIsDrawerOpen(true),
        nextStepDisabled: false,
        nextStepAction: () => {
          handleSubmitAllForms()
        },
        prevStepLabel: 'انصراف',
        nextStepLabel: 'ذخیره'
      }))
    } else {
      setButtonActions((prevState) => ({
        ...prevState,
        prevStepDisabled: false,
        nextStepDisabled: false,
        nextStepAction: () => {
          handleSubmitAllForms()
        },
        prevStepAction: () => {
          navigate('/merchant-register/category')
        },
        prevStepLabel: 'مرحله قبلی',
        nextStepLabel: 'مرحله بعدی'
      }))
    }
  }, [setButtonActions, formik.values, navigate, correction?.length])

  return (
    <div>
      <Drawer
        header={{
          haveCloseIcon: true,
          title: 'آیا از ذخیره تغییرات خود منصرف شدید؟',
          containerClassName: '[&>div]:right-36'
        }}
        onClose={() => {}}
        position="center"
        open={isDrawerOpen}
        width={400}
      >
        <div className="dgs-ui-kit-flex dgs-ui-kit-justify-end dgs-ui-kit-gap-2">
          <Button
            onClick={() => {
              setIsDrawerOpen(false)
              navigate('/merchant-register/status')
            }}
            isFullWidth
            variant="outline"
          >
            بله
          </Button>
          <Button
            onClick={() => setIsDrawerOpen(false)}
            isFullWidth
            variant="outline"
          >
            خیر
          </Button>
        </div>
      </Drawer>

      <form autoComplete="on" onSubmit={formik.handleSubmit}>
        <OfficeAddressForm
          formik={formik}
          provinces={provinces?.results ?? []}
        />

        <div className="my-5">
          <Switch
            className="!ring-0"
            label="آدرس انبار متفاوت است"
            onChange={handleWarehouseCheckboxChange}
            checked={showWarehouseAddress}
            size="large"
          />
        </div>

        {showWarehouseAddress && (
          <WarehouseAddressForm
            formik={formik}
            provinces={provinces?.results ?? []}
          />
        )}
      </form>
    </div>
  )
}

export default AddressForm
